// the theme's curve, read from CSS so the two can't drift apart
const EASE_OUT =
  getComputedStyle(document.documentElement).getPropertyValue('--ease-out').trim() ||
  'cubic-bezier(0.23, 1, 0.32, 1)';

/* Tab switching for the "where to begin" section.
   Progressive enhancement: without JS every panel renders stacked and readable;
   this element hides all but the selected one and wires ARIA + keyboard. */
class RisifyTabs extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready) return;
    this.tabs = [...this.querySelectorAll('[role="tab"]')];
    this.panels = this.tabs
      .map((tab) => this.querySelector(`#${CSS.escape(tab.getAttribute('aria-controls'))}`))
      .filter(Boolean);
    if (this.tabs.length !== this.panels.length || !this.tabs.length) return;

    this.tabs.forEach((tab, i) => {
      tab.addEventListener('click', () => this.select(i));
      tab.addEventListener('keydown', (e) => this.onKeydown(e, i));
    });
    this.dataset.ready = 'true';
    // the initial pick isn't a swap — nothing was on screen to replace
    this.select(0, { focus: false, animate: false });
  }

  onKeydown(event, index) {
    const last = this.tabs.length - 1;
    let next = null;
    if (event.key === 'ArrowRight') next = index === last ? 0 : index + 1;
    else if (event.key === 'ArrowLeft') next = index === 0 ? last : index - 1;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = last;
    if (next === null) return;
    event.preventDefault();
    this.select(next);
  }

  select(index, { focus = true, animate = true } = {}) {
    this.tabs.forEach((tab, i) => {
      const selected = i === index;
      tab.setAttribute('aria-selected', selected ? 'true' : 'false');
      // only the selected tab stays in the tab order (roving tabindex)
      tab.setAttribute('tabindex', selected ? '0' : '-1');
      this.panels[i].hidden = !selected;
    });
    // a full-height panel of image + copy replacing another in one frame is the
    // most jarring moment on the page; a short fade covers the swap
    if (animate && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.panels[index].animate(
        { opacity: [0, 1], transform: ['translateY(0.4rem)', 'none'] },
        { duration: 150, easing: EASE_OUT }
      );
    }
    if (focus) this.tabs[index].focus();
  }
}

customElements.define('risify-tabs', RisifyTabs);
